import { Button, Grid, Step, StepLabel, Stepper } from '@material-ui/core';
import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { clearSignupData, requestSignup } from '../../../redux/actions/signUpActions';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import Layout from '../Layout';
import { ROOT } from '../../../constants/routes';
import RegistrationFinish from './RegistrationFinish';
import RegistrationPassword from './RegistrationPassword';
import RegistrationStart from './RegistrationStart';
import { RootState } from '../../../types/stateTypes';
import commonStyleRules from '../styles';
import styleRules from './styles';
import { useAppSelector } from '../../../hooks/hooks';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => createStyles({ ...commonStyleRules(theme), ...styleRules(theme) }));

const Registration: FunctionComponent = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const backArrow = `${ROOT}/Auth/arrow-left.svg`;
  const regError = useAppSelector((state: RootState) => state.signUp.errorText);
  const loading = useAppSelector((state: RootState) => state.signUp.loading);
  const success = useAppSelector((state: RootState) => state.signUp.success);
  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const steps = [0, 1, 2];
  const navigation = useHistory();

  const firstStep = 0;
  const submitFormStep = steps.length - 2;
  const lastStep = steps.length - 1;
  const nextButtonContent = activeStep === lastStep ? t('auth.got.it') : t('auth.next');

  const handleNext = useCallback(() => {
    if (activeStep === lastStep) {
      dispatch(clearSignupData());
      navigation.push('/');
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  }, [activeStep, dispatch, lastStep, navigation]);

  const handleBack = () => {
    if (activeStep === firstStep) navigation.push('/');
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const submitForm = () => {
    dispatch(requestSignup({ email, password }));
  };

  const finishPage = <RegistrationFinish nextButtonContent={nextButtonContent} handleNext={handleNext} />;

  useEffect(() => {
    if (activeStep === submitFormStep && success) handleNext();
  }, [activeStep, handleNext, loading, submitFormStep, success]);

  const renderContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <RegistrationStart
            email={email}
            setEmail={setEmail}
            nextButtonContent={nextButtonContent}
            handleNext={handleNext}
          />
        );
      case 1:
        return (
          <RegistrationPassword
            passwordValue={password}
            passwordConfirmValue={passwordConfirm}
            setPassword={setPassword}
            setPasswordConfirm={setPasswordConfirm}
            nextButtonContent={nextButtonContent}
            handleNext={submitForm}
            loading={loading}
            submitError={regError}
          />
        );
      case 2:
      default:
        return finishPage;
    }
  };

  const { stepper, step, header, left, backButton, backImage } = useStyles();

  return (
    <Layout>
      <Grid container item direction="column" sm={12} md={5} xl={5} wrap="nowrap" className={left}>
        <Grid className={header} container item justifyContent="space-between" wrap="nowrap">
          <Grid container item>
            <Button className={backButton} disabled={activeStep === lastStep} onClick={handleBack}>
              <img className={backImage} src={backArrow} alt="back" />
            </Button>
          </Grid>
          <Grid container item justifyContent="flex-end">
            <Stepper connector={<></>} className={stepper} alternativeLabel activeStep={activeStep}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel className={step} />
                </Step>
              ))}
            </Stepper>
          </Grid>
        </Grid>
        {renderContent(activeStep)}
      </Grid>
    </Layout>
  );
};

export default Registration;
